// packages/app/src/components/admin/models/ArgentinaModelConfig.tsx
// AGC: Admin editor for the Argentina recommendation model (crop_model_config/AR).
// AGC: Weights, cited base_yield, and (?) explainers for every calculation. Auditable via versions.
// AGC: t() takes (key, interpolationOptions?) in this project — no string fallback arg; all
// AGC: display text comes from i18n keys (added in the i18n task) under admin.recommendations.ar.*
// AGC: Provenance section (read-only) fetches zones endpoint to surface ORA zone + phenology metadata.
import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from '../../../hooks'
import { Save, Loader2 } from 'lucide-react'
import { SectionCard } from './SectionCard'
import { ParamHelp } from './ParamHelp'
import { getBackendUrl } from '../../../utils/backendUrl'
import { useAuth } from '../../../hooks/useAuth'
import {
  DEFAULT_ARGENTINA_MODEL_CONFIG,
  type ArgentinaModelConfigType,
  type ArgentinaWeights,
} from '../../../types/admin/models'

const WEIGHT_KEYS: Array<keyof ArgentinaWeights> = ['water_balance', 'season_match', 'thermal_match', 'soil_match', 'historical_consistency']

// AGC: Provenance types — shape mirrors GET /api/v1/admin/config/crop-model/AR/zones response.
interface ProvenanceCrop {
  crop: string
  method: string
  sowing_window: string
  kc_source: string | null
  sowing_source: string | null
}

interface ProvenanceZone {
  code: string
  name: string
  region: string | null
  provinces: string[]
  repr_lat: number
  repr_lon: number
  crops: ProvenanceCrop[]
}

export const ArgentinaModelConfig: React.FC = () => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()
  // AGC: build auth headers per request with a fresh Firebase ID token (admin gate)
  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const token = await getAuthToken()
    return { Authorization: `Bearer ${token ?? ''}`, 'Content-Type': 'application/json' }
  }, [getAuthToken])
  const [config, setConfig] = useState<ArgentinaModelConfigType>(DEFAULT_ARGENTINA_MODEL_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // AGC: Provenance state — zones loaded from the read-only zones endpoint (AR-only, no edit).
  const [zones, setZones] = useState<ProvenanceZone[]>([])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/crop-model/AR`, {
        headers: await authHeaders(),
      })
      if (res.status === 401 || res.status === 403) {
        setError(t(res.status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return
      }
      const data = await res.json()
      if (data.success && data.data) setConfig({ ...DEFAULT_ARGENTINA_MODEL_CONFIG, ...data.data })
    } catch {
      setError(t('admin.recommendations.ar.loadError'))
    } finally { setLoading(false) }
  }, [t, authHeaders])

  // AGC: Fetch provenance zones separately — read-only, does not affect config loading state.
  const loadZones = useCallback(async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/crop-model/AR/zones`, {
        headers: await authHeaders(),
      })
      if (!res.ok) return // AGC: provenance is non-fatal — stay empty on auth/other failure.
      const data = await res.json()
      if (data.success && Array.isArray(data.data?.zones)) {
        setZones(data.data.zones as ProvenanceZone[])
      }
    } catch {
      // AGC: Non-fatal — provenance section simply stays empty if the endpoint is unreachable.
    }
  }, [authHeaders])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadZones() }, [loadZones])

  const save = async () => {
    setSaving(true); setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/crop-model/AR`, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({ config, updatedBy: 'admin' }),
      })
      if (res.status === 401 || res.status === 403) {
        setError(t(res.status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return
      }
      const data = await res.json()
      if (!data.success) setError((data.details || []).join(', ') || data.error)
    } catch {
      setError(t('admin.recommendations.ar.saveError'))
    } finally { setSaving(false) }
  }

  // AGC: [AR] Publish an immutable, auditable snapshot of the current config.
  const publishVersion = async () => {
    setPublishing(true); setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/crop-model/AR/versions`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ snapshot: config, note: '', publishedBy: 'admin' }),
      })
      if (res.status === 401 || res.status === 403) {
        setError(t(res.status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return
      }
    } catch {
      setError(t('admin.recommendations.ar.versionError'))
    } finally { setPublishing(false) }
  }

  const weightSum = WEIGHT_KEYS.reduce((s, k) => s + (config.weights[k] || 0), 0)

  // AGC: [AR] base_yield editing — flatten config.base_yield to rows, rebuild on change.
  // AGC: source is REQUIRED (NO INVENTAR); a row without source blocks save (server also validates).
  type YieldRow = { crop: string; zone: string; value: string; source: string }
  // AGC: renamed destructured param from 'zones' to 'zoneMap' to avoid shadowing the provenance zones state.
  const yieldRows: YieldRow[] = Object.entries(config.base_yield || {}).flatMap(([crop, zoneMap]) =>
    Object.entries(zoneMap).map(([zone, e]) => ({ crop, zone, value: String(e.value ?? ''), source: e.source ?? '' }))
  )
  const rebuildBaseYield = (rows: YieldRow[]) => {
    const next: ArgentinaModelConfigType['base_yield'] = {}
    for (const r of rows) {
      if (!r.crop || !r.zone) continue
      // AGC: build via a local map so TS narrows the nested object (strict mode).
      const cropMap = next[r.crop] || {}
      cropMap[r.zone] = { value: parseFloat(r.value) || 0, unit: 'kg/ha', source: r.source }
      next[r.crop] = cropMap
    }
    setConfig((c) => ({ ...c, base_yield: next }))
  }
  const setYieldRow = (idx: number, patch: Partial<YieldRow>) => rebuildBaseYield(yieldRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  const addYieldRow = () => rebuildBaseYield([...yieldRows, { crop: config.crops[0] || 'soja', zone: '', value: '', source: '' }])
  const removeYieldRow = (idx: number) => rebuildBaseYield(yieldRows.filter((_, i) => i !== idx))
  const yieldMissingSource = yieldRows.some((r) => r.zone && !r.source)

  if (loading) return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}</div>

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200">
        🇦🇷 {t('admin.recommendations.ar.title')}
      </h3>
      {error && <div className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      <SectionCard title={t('admin.recommendations.ar.weights')} icon="">
        {WEIGHT_KEYS.map((k) => (
          <div key={k} className="flex items-center justify-between py-2">
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {t(`admin.recommendations.ar.weight.${k}`)}
              <ParamHelp
                text={t(`admin.recommendations.ar.help.${k}`)}
                formula={k === 'water_balance' ? 'score = min(1, precip_ciclo / Σ(Kc·ET0·días)) · 100' : k === 'season_match' ? 'score = 100 si el mes está en la ventana de siembra ORA; si no, penaliza por distancia de meses' : undefined}
                source={t(`admin.recommendations.ar.source.${k}`)}
              />
            </span>
            <input
              type="number" step="0.05" min="0" max="1" value={config.weights[k]}
              onChange={(e) => setConfig((c) => ({ ...c, weights: { ...c.weights, [k]: parseFloat(e.target.value) || 0 } }))}
              className="w-24 px-2 py-1 rounded border border-slate-300 dark:bg-slate-700"
            />
          </div>
        ))}
        <div className={`mt-2 text-xs ${Math.abs(weightSum - 1) > 0.001 ? 'text-red-600' : 'text-slate-500'}`}>
          {t('admin.recommendations.ar.weightSum')}: {weightSum.toFixed(2)} (= 1.00)
        </div>
        <div className="mt-1 text-xs text-amber-600">
          {t('admin.recommendations.ar.deferredNote')}
        </div>
      </SectionCard>

      <SectionCard title={t('admin.recommendations.ar.minScore')} icon="">
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-slate-700 dark:text-slate-300">
            {t('admin.recommendations.ar.minScoreLabel')}
            <ParamHelp text={t('admin.recommendations.ar.help.min_score_threshold')} />
          </span>
          <input type="number" step="1" min="0" max="100" value={config.min_score_threshold ?? 55}
            onChange={(e) => setConfig((c) => ({ ...c, min_score_threshold: parseInt(e.target.value, 10) || 0 }))}
            className="w-24 px-2 py-1 rounded border border-slate-300 dark:bg-slate-700" />
        </div>
      </SectionCard>

      {/* AGC: [AR][RIEGO] Editable irrigation yield factor (applied when the lot toggles riego ON). */}
      <SectionCard title={t('admin.recommendations.ar.irrigationFactor')} icon="">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{t('admin.recommendations.ar.irrigationFactorNote')}</p>
        <div className="grid grid-cols-12 gap-2 items-center">
          <input
            type="number" step="0.01" min="1" className="col-span-3 px-2 py-1 rounded border border-slate-300 dark:bg-slate-700"
            value={config.irrigation_yield_factor?.value ?? 1.15}
            onChange={(e) => setConfig((c) => ({ ...c, irrigation_yield_factor: { value: parseFloat(e.target.value) || 1, source: c.irrigation_yield_factor?.source || '' } }))}
          />
          <input
            className="col-span-9 px-2 py-1 rounded border border-slate-300 dark:bg-slate-700"
            placeholder={t('admin.recommendations.ar.sourcePlaceholder')}
            value={config.irrigation_yield_factor?.source ?? ''}
            onChange={(e) => setConfig((c) => ({ ...c, irrigation_yield_factor: { value: c.irrigation_yield_factor?.value ?? 1.15, source: e.target.value } }))}
          />
        </div>
      </SectionCard>

      <SectionCard title={t('admin.recommendations.ar.baseYield')} icon="">
        <span className="text-xs text-slate-500">
          {t('admin.recommendations.ar.baseYieldNote')}
          <ParamHelp text={t('admin.recommendations.ar.help.base_yield')} />
        </span>
        {yieldRows.map((row, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center py-1">
            <input className="col-span-3 px-2 py-1 rounded border border-slate-300 dark:bg-slate-700" placeholder={t('admin.recommendations.ar.crop')} value={row.crop} onChange={(e) => setYieldRow(idx, { crop: e.target.value })} />
            <input className="col-span-2 px-2 py-1 rounded border border-slate-300 dark:bg-slate-700" placeholder={t('admin.recommendations.ar.zone')} value={row.zone} onChange={(e) => setYieldRow(idx, { zone: e.target.value })} />
            <input type="number" className="col-span-2 px-2 py-1 rounded border border-slate-300 dark:bg-slate-700" placeholder="kg/ha" value={row.value} onChange={(e) => setYieldRow(idx, { value: e.target.value })} />
            <input className={`col-span-4 px-2 py-1 rounded border dark:bg-slate-700 ${row.zone && !row.source ? 'border-red-400' : 'border-slate-300'}`} placeholder={t('admin.recommendations.ar.sourcePlaceholder')} value={row.source} onChange={(e) => setYieldRow(idx, { source: e.target.value })} />
            <button className="col-span-1 text-slate-400 hover:text-red-600" onClick={() => removeYieldRow(idx)} aria-label="remove">✕</button>
          </div>
        ))}
        <button onClick={addYieldRow} className="mt-2 text-sm text-emerald-700 hover:underline">
          + {t('admin.recommendations.ar.addBaseYield')}
        </button>
      </SectionCard>

      {/* AGC: Provenance section — read-only; no inputs, no save. AR-only, Brazil untouched. */}
      <SectionCard title={t('admin.recommendations.ar.provenance')} icon="">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          {t('admin.recommendations.ar.provenanceNote')}
        </p>
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          {t('admin.recommendations.ar.zoneCount', { count: zones.length })}
        </p>
        {zones.length === 0 && (
          <p className="text-xs text-slate-400 italic">{t('common.loading')}</p>
        )}
        <div className="space-y-2">
          {zones.map((zone) => (
            <details key={zone.code} className="border border-slate-200 dark:border-slate-700 rounded-lg">
              <summary className="px-3 py-2 cursor-pointer select-none text-sm font-semibold text-slate-700 dark:text-slate-200">
                {/* AGC: Zone summary: code + name + region + provinces for quick scan. */}
                <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded mr-2">{zone.code}</span>
                {zone.name}
                {zone.region && (
                  <span className="ml-2 text-xs text-slate-500 dark:text-slate-400 font-normal">({zone.region})</span>
                )}
                {zone.provinces.length > 0 && (
                  <span className="ml-2 text-xs text-slate-400 font-normal">[{zone.provinces.join(', ')}]</span>
                )}
              </summary>
              <div className="px-3 pb-3 space-y-3 border-t border-slate-100 dark:border-slate-700 pt-2">
                {zone.crops.map((c) => (
                  <div key={c.crop} className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                    {/* AGC: Per-crop phenology block: crop id, method, window, then sources as muted text. */}
                    <div>
                      <span className="font-semibold">{c.crop}</span>
                      <span className="mx-1 text-slate-400">·</span>
                      <span>{c.method}</span>
                      <span className="mx-1 text-slate-400">·</span>
                      <span className="font-mono">{c.sowing_window}</span>
                    </div>
                    {c.kc_source && (
                      <div className="text-slate-400 dark:text-slate-500 pl-2 truncate" title={c.kc_source}>
                        Kc: {c.kc_source}
                      </div>
                    )}
                    {c.sowing_source && (
                      <div className="text-slate-400 dark:text-slate-500 pl-2 truncate" title={c.sowing_source}>
                        Sowing: {c.sowing_source}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      </SectionCard>

      <div className="flex items-center">
        <button onClick={save} disabled={saving || Math.abs(weightSum - 1) > 0.001 || yieldMissingSource}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('admin.recommendations.ar.save')}
        </button>
        <button onClick={publishVersion} disabled={publishing}
          className="ml-2 px-4 py-2 bg-slate-200 dark:bg-white/10 rounded-lg text-slate-700 dark:text-slate-300 disabled:opacity-50">
          {t('admin.recommendations.ar.publishVersion')}
        </button>
      </div>
    </div>
  )
}
